// The No matches / Several matches review on screen (CAL-5998): the tag a job
// card carries while employees wait for a review, the label and the buttons of a
// file on the Matches tab, the Status cell of the CSV viewer, and the words of
// the "No match found" prompt. Rules and copy live in unmatchedReview.ts.
import {
  PersonOffOutlined as PersonOffIcon,
  PersonSearchOutlined as PersonSearchIcon,
  UndoOutlined as UndoIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type {
  MergeReportKind,
  UnmatchedEmployeeStatus,
  UnmatchedKindSummary,
  UnmatchedReview,
  UnmatchedReviewAction,
} from "features/adminUsers/adminUsers";

import SoftTag from "./SoftTag";
import {
  employeeStateChip,
  plural,
  type RunHint,
  UNMATCHED_KIND_LABELS,
  UNMATCHED_REVIEW_MARK_HINT,
  unmatchedCardTags,
  unmatchedFileLabel,
  unmatchedReviewLines,
  unmatchedReviewSteps,
} from "./unmatchedReview";

function MultiLine({ lines }: { lines: string[] }) {
  return (
    <Box component="span" sx={{ whiteSpace: "pre-line" }}>
      {lines.join("\n")}
    </Box>
  );
}

/** Job card / Manage job Overview: one warning tag per file with employees to review. */
export function UnmatchedReviewTags({
  review,
  onOpen,
}: {
  review: UnmatchedReview | null;
  onOpen: () => void;
}) {
  const tags = unmatchedCardTags(review);
  if (!tags.length) return null;
  return (
    <>
      {tags.map((tag) => (
        <SoftTag
          key={tag.kind}
          tone="warning"
          icon={<PersonSearchIcon />}
          label={tag.label}
          title={<MultiLine lines={tag.lines} />}
          onClick={onOpen}
        />
      ))}
    </>
  );
}

/** Under the file name on the Matches tab: the state of its employees. */
export function UnmatchedReviewFileLabel({
  kind,
  summary,
  rows,
}: {
  kind: MergeReportKind;
  summary: UnmatchedKindSummary | null | undefined;
  rows: number | null | undefined;
}) {
  const label = unmatchedFileLabel(summary);
  if (!label || !summary) return null;
  const lines = [
    ...unmatchedReviewLines(kind, summary, rows),
    UNMATCHED_REVIEW_MARK_HINT,
  ];
  return (
    <Stack spacing={0.25} alignItems="flex-start" sx={{ mt: 0.5 }}>
      <SoftTag
        tone={label.tone}
        label={label.label}
        title={<MultiLine lines={lines} />}
      />
      {label.detail ? (
        <Typography variant="caption" color="text.secondary">
          {label.detail}
        </Typography>
      ) : null}
    </Stack>
  );
}

/** The "No match found" and "Undo" icon buttons of a file row. */
export function UnmatchedReviewActions({
  summary,
  canEdit,
  readOnlyReason,
  busy,
  disabled,
  onAct,
}: {
  summary: UnmatchedKindSummary | null | undefined;
  canEdit: boolean;
  /** Why the operator may not act, when canEdit is false. */
  readOnlyReason: string | null;
  busy: boolean;
  /** Another matches operation is running (a delete, a merge). */
  disabled: boolean;
  onAct: (action: UnmatchedReviewAction) => void;
}) {
  if (!summary) return null;
  const pending = summary.pending;
  const marked = summary.no_match;
  const readOnly = readOnlyReason ?? "Read-only access.";
  const markReason = !canEdit
    ? readOnly
    : pending === 0
      ? "Every employee of this file is in the Matches file or already reviewed."
      : `No match found: mark the ${plural(pending, "employee")} still to review as reviewed - none of them could be matched.`;
  const clearReason = !canEdit
    ? readOnly
    : `Undo: clear the reviewed mark of ${plural(marked, "employee")} (marks set by Merge reports included).`;
  const spinner = <CircularProgress size={14} color="inherit" />;
  return (
    <>
      <Tooltip title={markReason}>
        <span>
          <IconButton
            size="small"
            aria-label="No match found"
            disabled={!canEdit || pending === 0 || busy || disabled}
            onClick={() => onAct("mark")}
          >
            {busy ? spinner : <PersonOffIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      {marked > 0 ? (
        <Tooltip title={clearReason}>
          <span>
            <IconButton
              size="small"
              aria-label="Undo no match found"
              disabled={!canEdit || busy || disabled}
              onClick={() => onAct("clear")}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
    </>
  );
}

/** The Status cell of an employee in the CSV viewer. */
export function UnmatchedEmployeeStateChip({
  status,
  onOpenTaskLogs,
}: {
  status: UnmatchedEmployeeStatus | null | undefined;
  onOpenTaskLogs: (taskId: number, employeeId: string) => void;
}) {
  const chip = employeeStateChip(status);
  const rows = status?.state === "merged" ? status.rows : [];
  return (
    <Stack spacing={0.25} alignItems="flex-start">
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Tooltip title={chip.hint}>
          <Chip
            size="small"
            variant="outlined"
            color={chip.color}
            label={chip.label}
            sx={{ height: 20, fontSize: "0.68rem" }}
          />
        </Tooltip>
        {rows.map((row) =>
          row.task_id != null ? (
            <Tooltip
              key={`${row.index}-${row.task_id}`}
              title={`Open the Logs of task #${row.task_id} on this employee`}
            >
              <Chip
                size="small"
                variant="outlined"
                clickable
                label={`task #${row.task_id}`}
                onClick={() =>
                  onOpenTaskLogs(row.task_id as number, row.employee_id)
                }
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            </Tooltip>
          ) : null,
        )}
      </Stack>
      {status?.state === "no_match" &&
      (status.reviewed_by || status.reviewed_at) ? (
        <Typography variant="caption" color="text.secondary">
          by {status.reviewed_by || "an operator"}
          {status.reviewed_at
            ? ` at ${new Date(status.reviewed_at).toLocaleString()}`
            : ""}
        </Typography>
      ) : null}
      {rows.length ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace" }}
        >
          {rows
            .map((row) => `#${row.index} ${row.employee_id}`.trim())
            .join(", ")}
        </Typography>
      ) : null}
    </Stack>
  );
}

/** One caption line on the Matches tab: the resolution loop in four steps. */
export function UnmatchedReviewInstructions({ hint }: { hint: RunHint }) {
  const steps = unmatchedReviewSteps(hint)
    .map((step, index) => `${index + 1} ${step.replace(/\.$/, "")}`)
    .join(" - ");
  return (
    <Typography variant="caption" color="text.secondary">
      Unmatched employees: {steps}. {UNMATCHED_REVIEW_MARK_HINT}
    </Typography>
  );
}

/** The body of the confirm prompt behind No match found / Undo. */
export function UnmatchedReviewConfirmBody({
  kind,
  action,
  summary,
  hint,
}: {
  kind: MergeReportKind;
  action: UnmatchedReviewAction;
  summary: UnmatchedKindSummary | null | undefined;
  hint: RunHint;
}) {
  const file = UNMATCHED_KIND_LABELS[kind];
  if (action === "clear") {
    return (
      <Typography variant="body2">
        The {plural(summary?.no_match ?? 0, "employee")} of{" "}
        <strong>{file}</strong> marked as reviewed - no match found go back to
        &quot;to review&quot;. Marks set by Merge reports (No Match rows) are
        cleared too.
      </Typography>
    );
  }
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        None of the remaining {plural(summary?.pending ?? 0, "employee")} of{" "}
        <strong>{file}</strong> could be matched on the vendor site. They are
        marked as reviewed with your name and the time, so nobody looks for them
        again. An employee that later turns up in the Matches file counts as
        merged, whatever the mark says.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Before you press it, the usual way to resolve a file is:
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
        {unmatchedReviewSteps(hint).map((step) => (
          <Typography
            key={step}
            component="li"
            variant="body2"
            color="text.secondary"
          >
            {step}
          </Typography>
        ))}
      </Box>
    </Stack>
  );
}
